import React, { useEffect, useMemo, useState } from 'react';

import { CloseCircleOutlined, PlusCircleOutlined } from '@ant-design/icons';
import { css } from '@emotion/css';
import { schemas } from '@recurrency/core-api-schema';
import { SupplierTargetTypes, TenantUserPermission } from '@recurrency/core-api-schema/dist/common/enums';
import { TenantSettingKey } from '@recurrency/core-api-schema/dist/common/tenantSettings';
import { ProductGroupMinimumDTO } from '@recurrency/core-api-schema/dist/purchasing/common';
import { Form, notification, Typography } from 'antd';
import { FieldData } from 'rc-field-form/lib/interface';
import { theme } from 'theme';
import { useDebounce } from 'use-debounce/lib';

import { formatTargetValue } from 'pages/purchasing/PurchaseTargetsPage/PurchaseTargetLinesPage/sidePane/POSummariesPanelContent';
import { TargetTypeSelectOptions } from 'pages/purchasing/utils';

import { fuzzyFilterAndHighlight } from 'components/AsyncSelect/useAsyncSelectProps';
import { Button } from 'components/Button';
import { ActionButton } from 'components/Button/ActionButton';
import { Container } from 'components/Container';
import { DividerLine } from 'components/DividerLine';
import { responsiveFormLayout, SwitchFormItem } from 'components/FormItems';
import { Input } from 'components/Input';
import { ExternalLink, NotificationLink } from 'components/Links';
import { SmallLoader } from 'components/Loaders';
import { Modal } from 'components/Modal';
import { PropertyListItem } from 'components/PropertyListItem';
import { Select } from 'components/Select';
import { Tabs } from 'components/Tabs';
import { InfoTooltip } from 'components/Tooltip/InfoTooltip';

import { useCoreApi } from 'hooks/useApi';

import { coreApiFetch } from 'utils/api';
import { captureAndShowError } from 'utils/error';
import { EMPTY_VALUE_DASHES, fieldsToIdNameObj } from 'utils/formatting';
import { routes } from 'utils/routes';
import { getTenantSetting } from 'utils/tenantSettings';
import { track, TrackEvent } from 'utils/track';

import { SupplierLocationSettingsAuditLog } from './AuditLog/SupplierLocationSettingsAuditLog';
import { LastUpdated } from './LastUpdatedMoment';

export interface SupplierLocationTargetSettings {
  supplierId: string;
  locationId: string;
  targetType: SupplierTargetTypes;
  targetValue: number;
  minTargetType: SupplierTargetTypes;
  minTargetValue: number;
  reviewCycleDays: number;
  useProductGroupMinimums?: boolean;
  productGroupMinimums?: (ProductGroupMinimumDTO & { productGroupName?: string })[];
}

export interface SupplierLocationSettingsModalProps {
  supplierId: string;
  locationId: string;
  onClose: (newSettings?: SupplierLocationTargetSettings) => void;
}

interface TargetFormData {
  useProductGroupMinimums?: boolean;
  productGroupMinimums?: (ProductGroupMinimumDTO & { productGroupName?: string })[];
  targetType?: SupplierTargetTypes;
  targetValue?: number;
  minTargetType?: SupplierTargetTypes;
  minTargetValue?: number;
  reviewCycleDays?: number;
}

export function SupplierLocationSettingsModal({ supplierId, locationId, onClose }: SupplierLocationSettingsModalProps) {
  const [supplierLocationSettingForm] = Form.useForm<TargetFormData>();
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Form data is used for tracking to show warnings
  const [formData, setFormData] = useState<TargetFormData>();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedGroups, setSelectedGroups] = useState<Set<string>>(new Set());
  const [debouncedSearchQuery] = useDebounce(searchQuery, 300);
  const [validationErrors, setValidationErrors] = useState<Record<string, string[]>>({});
  const {
    data,
    isLoading,
    setData: setSettingsData,
  } = useCoreApi(schemas.purchasing.getSupplierLocationSettings, {
    queryParams: {
      supplierLocationIds: [`${supplierId}|${locationId}`],
    },
  });

  const { data: itemGroupsData } = useCoreApi(schemas.itemGroups.getItemGroups, {
    queryParams: {
      filter: {
        supplierLocationIds: [{ supplierId, locationId }],
      },
    },
  });

  // Maps product groupId to groupInfo
  const allOptions = useMemo(
    () =>
      (itemGroupsData?.items ?? []).reduce((acc, curr) => {
        acc[curr.itemGroupId] = {
          productGroupId: curr.itemGroupId,
          productGroupName: fieldsToIdNameObj(curr.itemGroupId, curr.itemGroupName || '') as string,
        };
        return acc;
      }, {} as Record<string, { productGroupId: string; productGroupName: string }>),
    [itemGroupsData?.items],
  );

  // Tracks which groups have been set to avoid duplicates
  useEffect(() => {
    if (!formData?.productGroupMinimums) setSelectedGroups(new Set());
    else setSelectedGroups(new Set(formData?.productGroupMinimums.map((item) => item?.productGroupId ?? '')));
  }, [formData?.productGroupMinimums]);

  // Difference of allOptions and selected PG's + search
  const getOptionsForRow = (currentRowProductGroupId?: string) => {
    let filtered = Object.values(allOptions)
      .filter((opt) => !selectedGroups.has(opt.productGroupId) || opt.productGroupId === currentRowProductGroupId)
      .map((opt) => ({ label: opt.productGroupName, value: opt.productGroupId }));
    if (debouncedSearchQuery.length > 0) {
      filtered = fuzzyFilterAndHighlight(filtered, debouncedSearchQuery, ['label']);
    }
    return filtered;
  };

  const settingsData = data?.items.find(
    (setting) => setting.supplierId === supplierId && setting.locationId === locationId,
  );

  const productGroupMinsEnabled = getTenantSetting(TenantSettingKey.FeaturePurchasingProductGroupMinimums);

  // fill in form data from api response
  useEffect(() => {
    if (settingsData) {
      // hide NOT_SET from selectable options
      const filteredSettingsData = {
        targetType: settingsData.targetType !== SupplierTargetTypes.NotSet ? settingsData.targetType : undefined,
        targetValue: settingsData.targetValue,
        minTargetType: settingsData.minTargetType,
        minTargetValue: settingsData.minTargetValue,
        reviewCycleDays: settingsData.reviewCycleDays,
        useProductGroupMinimums: (settingsData?.productGroupMinimums?.length ?? 0) > 0,
        productGroupMinimums: (settingsData?.productGroupMinimums ?? []).map((p) => ({
          ...p,
          productGroupName: allOptions[p.productGroupId]?.productGroupName ?? '',
        })),
      };
      supplierLocationSettingForm.setFieldsValue(filteredSettingsData);
      setFormData(filteredSettingsData);
    }
  }, [allOptions, itemGroupsData?.items, settingsData, supplierLocationSettingForm]);

  async function handleSubmit(data: TargetFormData) {
    try {
      setIsSubmitting(true);
      const isTargetValueSet = data.targetValue && Number(data.targetValue) > 0;
      const isMinTargetValueSet = data.minTargetValue && Number(data.minTargetValue) > 0;

      const newSettings: SupplierLocationTargetSettings = {
        supplierId,
        locationId,
        targetType: (isTargetValueSet ? data.targetType : SupplierTargetTypes.NotSet) as SupplierTargetTypes,
        targetValue: Number(data.targetValue),
        minTargetType: (isMinTargetValueSet ? data.minTargetType : SupplierTargetTypes.NotSet) as SupplierTargetTypes,
        minTargetValue: Number(data.minTargetValue),
        reviewCycleDays: Number(data.reviewCycleDays),
        productGroupMinimums: (data.productGroupMinimums ?? []).map((p) => ({
          minimumTargetValue: Number(p.minimumTargetValue) ?? 1,
          productGroupId: p.productGroupId ?? '',
        })),
      };
      // Update cache for optimistic
      setSettingsData({ items: [{ ...settingsData, ...newSettings }], totalCount: 1 });
      await coreApiFetch(schemas.purchasing.patchSupplierLocationSettings, { bodyParams: newSettings });
      notification.success({
        key: supplierId,
        message: (
          <>
            <div>Updated target & review settings</div>
            <NotificationLink
              notificationKey={supplierId}
              to={routes.purchasing.purchaseTargets({
                supplierIds: [supplierId],
                locationIds: [locationId],
              })}
            >
              View targets for Supplier #{supplierId} at Location #{locationId}
            </NotificationLink>
          </>
        ),
        duration: 5,
      });
      // update api response data after patch
      setSettingsData({ items: [{ ...settingsData, ...newSettings }], totalCount: 1 });
      if (!!newSettings.minTargetType && !!newSettings.minTargetValue) {
        track(TrackEvent.SupplierMinimums_UpdateValues, { updateLocation: 'SupplierLocationSettingsModal' });
      }
      track(TrackEvent.Purchasing_SetSupplierLocationTarget_Submit, newSettings);
      onClose(newSettings);
    } catch (err) {
      captureAndShowError(err, 'Error updating settings', {
        notificationKey: supplierId,
        duration: 0,
      });
      setIsSubmitting(false);
    }
  }

  const dynamicListValidation = (_: FieldData[], allFields: FieldData[]) => {
    const newErrors: Record<string, string[]> = {};
    allFields.forEach((field) => {
      if (field.errors && field.errors.length > 0) {
        const fieldKey = Array.isArray(field.name) ? field.name.join('.') : field.name;
        newErrors[fieldKey] = field.errors;
      }
    });
    setValidationErrors(newErrors);
  };

  // Only show warning if all values are set and the flag is enabled
  const minLessThanTargetWarning =
    formData?.minTargetValue &&
    formData.minTargetType &&
    formData.targetType &&
    formData.targetValue &&
    formData.minTargetType === formData.targetType &&
    formData.minTargetValue > formData.targetValue;

  return (
    <Modal
      visible
      title={`Edit Settings for Supplier #${supplierId} at Location #${locationId}`}
      onCancel={() => onClose()}
      centered
      width={900}
      footer={
        <>
          <Button key="cancel" onClick={() => onClose()}>
            Cancel
          </Button>
          <Button
            key="submit"
            type="primary"
            permissionsRequired={[TenantUserPermission.ActionEditItemSupplierDetails]}
            form="supplierLocationSettingForm"
            htmlType="submit"
            disabled={isLoading}
            loading={isSubmitting}
          >
            Set Target
          </Button>
        </>
      }
    >
      <>
        {isLoading ? (
          <SmallLoader />
        ) : (
          <Container>
            <LastUpdated
              entityName="Settings"
              dateString={settingsData?.updatedAt}
              showIcon
              className={css`
                margin-bottom: 24px;
              `}
            />

            <Form.Provider>
              <Form
                name="supplierLocationSettingForm"
                form={supplierLocationSettingForm}
                onFinish={handleSubmit}
                onError={console.error}
                onFinishFailed={console.error}
                onValuesChange={(_, allValues) => setFormData(allValues)}
                onFieldsChange={dynamicListValidation}
                {...responsiveFormLayout}
              >
                <>
                  <Typography.Title level={5}>Target Value</Typography.Title>
                  <Typography.Paragraph>
                    Recurrency will mark a supplier “Ready for Review” when the line item requirements sum to this
                    threshold. Recurrency also uses this target to recommend item quantity changes in PO Auto Fill and
                    Auto Trim. This target is typically used to reflect a supplier’s pre-paid freight threshold, minimum
                    order quantity, full container volume, or other purchasing goal.
                  </Typography.Paragraph>
                  <div className={optionWrapperCss}>
                    <div className={valuesWrapperCss}>
                      <PropertyListItem
                        label="Current Target Value"
                        value={
                          settingsData?.targetType && settingsData?.targetType !== SupplierTargetTypes.NotSet
                            ? formatTargetValue(settingsData.targetType, settingsData.targetValue ?? 0)
                            : EMPTY_VALUE_DASHES
                        }
                      />
                      <PropertyListItem
                        label="New Target Value"
                        value={
                          <Form.Item
                            className={noMarginCss}
                            name="targetValue"
                            validateStatus={minLessThanTargetWarning ? 'warning' : undefined}
                            hasFeedback={!!minLessThanTargetWarning}
                            help={
                              minLessThanTargetWarning
                                ? 'Target value should be greater than or equal to minimum value.'
                                : undefined
                            }
                          >
                            <Input
                              type="number"
                              allowClear
                              min={0}
                              onChange={(ev) => setFormData({ ...formData, targetValue: Number(ev.target.value) })}
                            />
                          </Form.Item>
                        }
                      />
                    </div>
                    <div className={valuesWrapperCss}>
                      <PropertyListItem
                        label="Current Target Type"
                        value={
                          settingsData?.targetType && settingsData?.targetType !== SupplierTargetTypes.NotSet
                            ? settingsData.targetType.toUpperCase()
                            : EMPTY_VALUE_DASHES
                        }
                      />
                      <PropertyListItem
                        label="New Target Type"
                        value={
                          <Form.Item
                            className={noMarginCss}
                            name="targetType"
                            rules={[
                              {
                                validator: (_, value) => {
                                  if (typeof formData?.targetValue === 'number' && !value) {
                                    return Promise.reject(new Error('Target type is required.'));
                                  }
                                  return Promise.resolve();
                                },
                              },
                            ]}
                          >
                            <Select
                              options={TargetTypeSelectOptions}
                              onChange={(option) => setFormData({ ...formData, targetType: option })}
                            />
                          </Form.Item>
                        }
                      />
                    </div>
                  </div>

                  <DividerLine />

                  <Typography.Title level={5}>Supplier Minimum</Typography.Title>
                  <Typography.Paragraph>
                    When creating purchase orders for this supplier and location, Recurrency will show a warning if the
                    supplier minimum isn’t reached after changing the item quantities. Learn more{' '}
                    <ExternalLink to="https://help.recurrency.com/hc/en-us/articles/33749397573403-Supplier-Minimums">
                      here
                    </ExternalLink>
                    .
                  </Typography.Paragraph>
                  {productGroupMinsEnabled && (
                    <div
                      className={css`
                        padding-bottom: 16px;
                      `}
                    >
                      <SwitchFormItem
                        label={
                          <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                            Use product group minimums for this supplier
                            <InfoTooltip
                              title="When a product group is selected in purchasing, this value will be used instead of the supplier minimum."
                              placement="top"
                            />
                          </span>
                        }
                        name="useProductGroupMinimums"
                        onChange={(useProductGroupMinimums) => setFormData({ ...formData, useProductGroupMinimums })}
                      />
                    </div>
                  )}
                  {formData?.useProductGroupMinimums ? (
                    <Form.List name="productGroupMinimums" initialValue={formData?.productGroupMinimums ?? []}>
                      {(fields, { add, remove }) => (
                        <>
                          {fields.map(({ name, key, ...field }, index) => {
                            const targetErrors =
                              validationErrors[`productGroupMinimums.${name}.minimumTargetValue`] || [];

                            const groupErrors = validationErrors[`productGroupMinimums.${name}.productGroupId`] || [];
                            return (
                              <div key={key + index} className={containerCss}>
                                <div className={fieldColumnCss}>
                                  <Form.Item
                                    {...field}
                                    label="Product Group"
                                    name={[name, 'productGroupId']}
                                    noStyle
                                    hasFeedback
                                    validateTrigger={['onSubmit']}
                                    rules={[
                                      {
                                        required: true,
                                        message: 'Select a product group',
                                        validateTrigger: 'onSubmit',
                                      },
                                    ]}
                                  >
                                    <Select
                                      placeholder="Select product group"
                                      style={{ width: '100%', height: 40 }}
                                      showSearch
                                      optionFilterProp="label"
                                      optionLabelProp="label"
                                      options={getOptionsForRow(
                                        formData?.productGroupMinimums?.[index]?.productGroupId,
                                      )}
                                      onSearch={(input) => setSearchQuery(input)}
                                      className={selectErrorBorderCss(groupErrors.length > 0)}
                                    />
                                  </Form.Item>
                                  <ErrorMessageContainer errors={groupErrors} />
                                </div>
                                <div className={fieldColumnCss}>
                                  <Form.Item
                                    noStyle
                                    label="Minimum Value"
                                    {...field}
                                    name={[name, 'minimumTargetValue']}
                                    className={noMarginCss}
                                    validateTrigger="onSubmit"
                                    rules={[
                                      {
                                        validator: (_, value) => {
                                          if (Number.isNaN(Number(value)) || Number(value) < 1) {
                                            return Promise.reject(new Error('Minimum value must be greater than 0.'));
                                          }
                                          return Promise.resolve();
                                        },
                                      },
                                    ]}
                                  >
                                    <Input
                                      addonBefore="$"
                                      type="number"
                                      className={inputErrorBorderCss(targetErrors.length > 0)}
                                    />
                                  </Form.Item>
                                  <ErrorMessageContainer errors={targetErrors} />
                                </div>
                                <ActionButton
                                  className={closeButtonCss}
                                  label={<CloseCircleOutlined />}
                                  onClick={() => remove(name)}
                                />
                              </div>
                            );
                          })}
                          <Form.Item hidden={fields.length === Object.values(allOptions).length}>
                            <ActionButton
                              onClick={() => add()}
                              className={css`
                                display: inline-block;
                              `}
                              label={
                                <>
                                  <PlusCircleOutlined /> Add product group minimum
                                </>
                              }
                            />
                          </Form.Item>
                        </>
                      )}
                    </Form.List>
                  ) : (
                    <div className={optionWrapperCss}>
                      <div className={valuesWrapperCss}>
                        <PropertyListItem
                          label="Current Minimum Value"
                          value={
                            settingsData?.minTargetValue
                              ? formatTargetValue(
                                  settingsData?.minTargetType ?? SupplierTargetTypes.NotSet,
                                  settingsData.minTargetValue,
                                )
                              : EMPTY_VALUE_DASHES
                          }
                        />
                        <PropertyListItem
                          label="New Minimum Value"
                          value={
                            <Form.Item
                              className={noMarginCss}
                              name="minTargetValue"
                              validateStatus={minLessThanTargetWarning ? 'warning' : undefined}
                              hasFeedback={!!minLessThanTargetWarning}
                              help={
                                minLessThanTargetWarning
                                  ? 'Minimum value should be less than or equal to target value.'
                                  : undefined
                              }
                            >
                              <Input
                                type="number"
                                allowClear
                                min={0}
                                onChange={(ev) => setFormData({ ...formData, minTargetValue: Number(ev.target.value) })}
                              />
                            </Form.Item>
                          }
                        />
                      </div>
                      <div className={valuesWrapperCss}>
                        <PropertyListItem
                          label="Current Minimum Type"
                          value={
                            settingsData?.minTargetType ? settingsData.minTargetType.toUpperCase() : EMPTY_VALUE_DASHES
                          }
                        />
                        <PropertyListItem
                          label="New Minimum Type"
                          value={
                            <Form.Item
                              className={noMarginCss}
                              name="minTargetType"
                              rules={[
                                {
                                  validator: (_, value) => {
                                    if (typeof formData?.minTargetValue === 'number' && !value) {
                                      return Promise.reject(new Error('Minimum type is required.'));
                                    }
                                    return Promise.resolve();
                                  },
                                },
                              ]}
                            >
                              <Select
                                options={TargetTypeSelectOptions}
                                onChange={(option) => setFormData({ ...formData, minTargetType: option })}
                              />
                            </Form.Item>
                          }
                        />
                      </div>
                    </div>
                  )}

                  <DividerLine />

                  <Typography.Title level={5}>Review Cycle</Typography.Title>
                  <Typography.Paragraph>
                    Recurrency will mark a supplier “Ready for Review” based on this configurable review cadence. Every
                    time a PO is created in Recurrency or a supplier is “Marked as Reviewed,” the cycle will reset to
                    begin on that day.
                  </Typography.Paragraph>
                  <div className={valuesWrapperCss}>
                    <PropertyListItem label="Current Review Cycle (days)" value={settingsData?.reviewCycleDays} />
                    <PropertyListItem
                      label="New Review Cycle (days)"
                      value={
                        <Form.Item
                          className={noMarginCss}
                          name="reviewCycleDays"
                          rules={[
                            {
                              required: true,
                              message: 'Review cycle days required.',
                            },
                          ]}
                        >
                          <Input type="number" min={0} />
                        </Form.Item>
                      }
                    />
                  </div>
                </>
              </Form>
            </Form.Provider>
          </Container>
        )}
        <DividerLine />
        <Tabs
          tabs={[
            {
              header: 'Audit Log',
              infoTooltip: `All updates for Supplier #${supplierId} at Location #${locationId}`,
              content: <SupplierLocationSettingsAuditLog locationId={locationId} supplierId={supplierId} />,
            },
          ]}
        />
      </>
    </Modal>
  );
}

const ErrorMessageContainer = ({ errors = [] }: { errors: string[] }) =>
  errors.length > 0 ? (
    <div
      className={css`
        color: ${theme.colors.danger[500]};
        font-size: 14px;
        line-height: 1.6;
        white-space: nowrap;
      `}
    >
      {errors[0]}
    </div>
  ) : null;

/// /// shared styles //////

const optionWrapperCss = css`
  display: flex;

  & > * {
    flex: 1;
  }
`;

const valuesWrapperCss = css`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const noMarginCss = css`
  margin: 0;
`;
const containerCss = css`
  display: inline-flex;
  flex-direction: row;
  gap: 10px;
  margin-top: 10px;
  align-items: flex-start;
  justify-content: flex-start;
  width: 100%;
  padding-bottom: 10px;
`;

const fieldColumnCss = css`
  display: flex;
  flex-direction: column;
  flex: 1;
  gap: 4px;
`;

const errorBorderColor = `border-color: ${theme.colors.danger[500]} !important;`;
const inputErrorBorderCss = (error: boolean) => css`
  width: 100%;
  border-radius: 8px 0px 8px 0px;
  ${error && errorBorderColor}

  .ant-input-wrapper.ant-input-group {
    height: 40px;
    ${error && errorBorderColor}
  }
  .ant-input-group-addon {
    height: 40px;
    border-radius: 8px 0px 0px 8px;
    ${error && errorBorderColor}
  }
  .ant-input {
    height: 40px !important;
    border-radius: 0px 8px 8px 0px;
    ${error && errorBorderColor}
  }
`;

const selectErrorBorderCss = (error: boolean) => css`
  ${error && errorBorderColor}
  .ant-select-selector {
    ${error && errorBorderColor}
    height: 40px !important;
  }
`;
const closeButtonCss = css`
  padding-left: 8px;
  font-size: 16px;
  color: ${theme.colors.danger[600]};
  align-self: flex-start;
  margin-top: 8px;
  flex-shrink: 0 !important;
  &:hover {
    color: ${theme.colors.danger[700]};
  }
`;
